import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

function EventsJsonLd() {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/portfolio/events#collection`,
      name: 'Events - KAZU.PHOTO',
      description: 'イベント撮影コレクション。演奏会、オーケストラ、ヨガイベント、地域イベント、式典、トークイベントなど、人が集まる時間と空気を記録しています。',
      url: `${SITE_URL}/portfolio/events`,
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Portfolio', item: `${SITE_URL}/#portfolio` },
          { '@type': 'ListItem', position: 3, name: 'Events', item: `${SITE_URL}/portfolio/events` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-events';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('jsonld-events');
      if (existing) existing.parentNode?.removeChild(existing);
    };
  }, []);

  return null;
}

export default function EventsGallery() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const galleryImages = [
    {
      url: 'https://public.readdy.ai/ai/img_res/f247f98db7693caac192526e023e1cad.jpg',
      alt: 'オーケストラ演奏会の全景',
      aspect: 'aspect-[3/2]',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=close-up%20documentary%20photography%20of%20violinist%20hands%20playing%20violin%20during%20orchestra%20performance%2C%20focused%20on%20instrument%20and%20fingers%2C%20warm%20stage%20lighting%2C%20shallow%20depth%20of%20field%2C%20capturing%20the%20intensity%20and%20detail%20of%20live%20music%20performance%2C%20professional%20event%20photography%20style&width=800&height=1000&seq=events-gallery02&orientation=portrait',
      alt: 'バイオリン演奏の手元',
      aspect: 'aspect-[4/5]',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=documentary%20photography%20of%20yoga%20event%20in%20bright%20natural%20light%20studio%2C%20wide%20shot%20showing%20multiple%20participants%20in%20yoga%20poses%20on%20mats%2C%20peaceful%20and%20focused%20atmosphere%2C%20clean%20minimal%20space%2C%20professional%20event%20photography%20capturing%20wellness%20and%20mindfulness%20gathering&width=1200&height=800&seq=events-gallery03&orientation=landscape',
      alt: 'ヨガイベントの様子',
      aspect: 'aspect-[3/2]',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=candid%20documentary%20photography%20of%20audience%20members%20at%20cultural%20event%2C%20showing%20genuine%20expressions%20of%20concentration%20and%20emotion%2C%20soft%20natural%20lighting%2C%20capturing%20the%20human%20connection%20and%20engagement%20during%20live%20performance%2C%20professional%20event%20photography%20with%20shallow%20depth%20of%20field&width=800&height=1000&seq=events-gallery04&orientation=portrait',
      alt: '観客の表情',
      aspect: 'aspect-[4/5]',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=documentary%20photography%20of%20traditional%20Japanese%20community%20festival%20event%2C%20people%20gathering%20in%20outdoor%20setting%2C%20warm%20afternoon%20light%2C%20capturing%20cultural%20celebration%20and%20local%20community%20spirit%2C%20professional%20event%20photography%20with%20natural%20candid%20moments%20and%20authentic%20atmosphere&width=1200&height=800&seq=events-gallery05&orientation=landscape',
      alt: '地域イベントの風景',
      aspect: 'aspect-[3/2]',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=close-up%20detail%20photography%20of%20piano%20keys%20and%20pianist%20hands%20during%20concert%20performance%2C%20dramatic%20stage%20lighting%20creating%20contrast%2C%20capturing%20the%20artistry%20and%20precision%20of%20live%20music%2C%20professional%20event%20photography%20with%20artistic%20composition%20and%20shallow%20depth%20of%20field&width=800&height=1000&seq=events-gallery06&orientation=portrait',
      alt: 'ピアノ演奏のディテール',
      aspect: 'aspect-[4/5]',
    },
    {
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/638f4b01914fcab954051fd4cd63f313.jpeg',
      alt: 'トークイベントの登壇者',
      aspect: 'aspect-[3/2]',
    },
    {
      url: 'https://readdy.ai/api/search-image?query=atmospheric%20detail%20photography%20of%20concert%20hall%20interior%2C%20showing%20elegant%20architectural%20elements%2C%20warm%20ambient%20lighting%2C%20empty%20seats%20before%20performance%2C%20capturing%20the%20anticipation%20and%20quiet%20beauty%20of%20event%20venue%2C%20professional%20architectural%20event%20photography%20with%20cinematic%20mood&width=800&height=1000&seq=events-gallery08&orientation=portrait',
      alt: '会場の空気感',
      aspect: 'aspect-[4/5]',
    },
    {
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/bc2eae0d880182172254572f3095ffd2.jpeg',
      alt: 'イベント後の余韻',
      aspect: 'aspect-[3/2]',
    },
  ];

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  const handleContactClick = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="bg-white min-h-screen">
      <EventsJsonLd />

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'
        }`}
      >
        <div className="flex items-center justify-between px-8 lg:px-16 py-6">
          <Link
            to="/"
            className="text-xs tracking-[0.2em] font-light text-stone-900 hover:text-stone-600 transition-colors"
          >
            KAZU.PHOTO
          </Link>
          <Link
            to="/"
            className="text-[11px] tracking-[0.2em] font-light text-stone-600 hover:text-stone-900 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-0 overflow-hidden">
        <div className="relative h-[70vh] md:h-[80vh]">
          <img
            src="https://readdy.ai/api/search-image?query=cinematic%20wide%20angle%20documentary%20photography%20of%20live%20orchestra%20concert%20performance%20in%20elegant%20concert%20hall%2C%20musicians%20on%20stage%20with%20dramatic%20warm%20stage%20lighting%2C%20atmospheric%20mood%20with%20depth%2C%20professional%20event%20photography%20capturing%20the%20energy%20and%20scale%20of%20classical%20music%20performance%2C%20high%20quality%20composition&width=1600&height=900&seq=events-hero01&orientation=landscape"
            alt="Events Photography"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-8 lg:px-16 pb-16">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight tracking-wide text-white mb-6 drop-shadow-lg">
              Events
            </h1>
            <p className="text-sm md:text-base text-white/90 font-light tracking-wide leading-relaxed max-w-2xl">
              演奏会、オーケストラ、ヨガイベント、地域の催し。
              <br />
              人が集まる時間と、その場に流れる空気を記録しています。
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 lg:py-32 px-8 lg:px-16 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-base md:text-lg text-stone-600 font-light leading-relaxed tracking-wide">
            イベント撮影では、その場で起きている出来事だけでなく、
            <br />
            会場の空気、参加する人々の表情、
            <br />
            終わった後に残る余韻までを大切にしています。
            <br />
            <br />
            記録として残すだけでなく、その場の時間を
            <br />
            もう一度思い出せるような写真を目指しています。
          </p>
        </div>
      </section>

      {/* Highlight Section */}
      <section className="py-24 lg:py-32 px-8 lg:px-16 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extralight tracking-wide text-stone-900 mb-20 text-center">
            Event Highlights
          </h2>

          <div className="space-y-32">
            {/* Highlight 1 - Wide Shot */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-8">
                <div className="aspect-[3/2] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=atmospheric%20wide%20angle%20documentary%20photography%20of%20concert%20hall%20interior%20during%20performance%2C%20showing%20full%20venue%20scale%20with%20stage%20and%20audience%2C%20warm%20ambient%20lighting%20creating%20intimate%20atmosphere%2C%20professional%20event%20photography%20capturing%20the%20grandeur%20and%20energy%20of%20live%20music%20event&width=1200&height=800&seq=events-highlight01&orientation=landscape"
                    alt="会場全体の空気や規模感"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-4">
                <p className="text-sm text-stone-500 font-light leading-relaxed tracking-wide">
                  会場全体の空気や規模感。
                  <br />
                  その場にいた人だけが感じられる、
                  <br />
                  熱量と静けさ。
                </p>
              </div>
            </div>

            {/* Highlight 2 - Close-up */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-4 order-2 lg:order-1">
                <p className="text-sm text-stone-500 font-light leading-relaxed tracking-wide">
                  演奏者や登壇者など、
                  <br />
                  主役の瞬間。
                  <br />
                  集中と表現の一瞬を捉える。
                </p>
              </div>
              <div className="lg:col-span-8 order-1 lg:order-2">
                <div className="aspect-[3/2] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=intimate%20documentary%20photography%20of%20conductor%20leading%20orchestra%20during%20performance%2C%20focused%20on%20expressive%20gestures%20and%20concentration%2C%20dramatic%20stage%20lighting%2C%20professional%20event%20photography%20capturing%20the%20artistry%20and%20emotion%20of%20live%20music%20direction%20with%20shallow%20depth%20of%20field&width=1200&height=800&seq=events-highlight02&orientation=landscape"
                    alt="演奏者や登壇者の瞬間"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Highlight 3 - Audience */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-8">
                <div className="aspect-[3/2] rounded-2xl overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/f7fef8b96d63da9eb5490ab597dbbe2b.jpeg"
                    alt="参加者や観客の表情"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-4">
                <p className="text-sm text-stone-500 font-light leading-relaxed tracking-wide">
                  参加者や観客の表情。
                  <br />
                  笑顔、集中、感動。
                  <br />
                  その場を共有する人々の時間。
                </p>
              </div>
            </div>

            {/* Highlight 4 - Details */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-4 order-2 lg:order-1">
                <p className="text-sm text-stone-500 font-light leading-relaxed tracking-wide">
                  手元や照明、楽器、会場装飾。
                  <br />
                  細部に宿る美しさと、
                  <br />
                  準備された時間の痕跡。
                </p>
              </div>
              <div className="lg:col-span-8 order-1 lg:order-2">
                <div className="aspect-[3/2] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=artistic%20detail%20photography%20of%20cello%20strings%20and%20bow%20during%20orchestra%20performance%2C%20dramatic%20stage%20lighting%20creating%20beautiful%20contrast%2C%20capturing%20the%20craftsmanship%20and%20beauty%20of%20musical%20instruments%2C%20professional%20event%20photography%20with%20shallow%20depth%20of%20field%20and%20cinematic%20composition&width=1200&height=800&seq=events-highlight04&orientation=landscape"
                    alt="ディテールの美しさ"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Highlight 5 - Afterglow */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-8">
                <div className="aspect-[3/2] rounded-2xl overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/6fa13c432482f18b4d3e316ee5735a2c.jpeg"
                    alt="イベント後の余韻"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-4">
                <p className="text-sm text-stone-500 font-light leading-relaxed tracking-wide">
                  イベント後の余韻。
                  <br />
                  終わった後に残る、
                  <br />
                  静かな満足感と温かさ。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-24 lg:py-32 px-8 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extralight tracking-wide text-stone-900 mb-20 text-center">
            Photo Gallery
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {galleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => openLightbox(index)}
                className="group cursor-pointer relative overflow-hidden rounded-2xl bg-stone-50"
              >
                <div className={`${image.aspect} overflow-hidden`}>
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-zoom-in-line text-white text-2xl drop-shadow-lg" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Optional Film Section */}
      <section className="py-24 lg:py-32 px-8 lg:px-16 bg-stone-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extralight tracking-wide text-stone-900 mb-8">
            Event Film
          </h2>
          <p className="text-sm text-stone-500 font-light leading-relaxed tracking-wide">
            必要に応じて、イベントの様子を映像でも記録しています。
          </p>
          <div className="mt-16 aspect-video rounded-2xl bg-stone-200 flex items-center justify-center">
            <p className="text-xs text-stone-400 tracking-wider">Video content can be added here</p>
          </div>
        </div>
      </section>

      {/* Project Info Section */}
      <section className="py-24 lg:py-32 px-8 lg:px-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <p className="text-[10px] tracking-[0.3em] text-stone-400 uppercase mb-4">Content</p>
              <p className="text-sm text-stone-600 font-light leading-relaxed">
                演奏会 / オーケストラ / ヨガイベント / 地域イベント / 式典 / トークイベント
              </p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] text-stone-400 uppercase mb-4">Style</p>
              <p className="text-sm text-stone-600 font-light leading-relaxed">
                Documentary Event Photography
              </p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] text-stone-400 uppercase mb-4">Focus</p>
              <p className="text-sm text-stone-600 font-light leading-relaxed">
                空気感、熱量、余韻、人物の表情
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 lg:py-32 px-8 lg:px-16 bg-stone-50">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-stone-600 font-light leading-relaxed tracking-wide mb-8">
            イベント撮影のご相談は、お問い合わせページよりご連絡ください。
          </p>
          <button
            onClick={handleContactClick}
            className="inline-flex items-center gap-3 px-10 py-4 bg-stone-900 text-white text-sm tracking-wider rounded-full hover:bg-stone-800 transition-colors whitespace-nowrap"
          >
            お問い合わせ
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white transition-colors"
          >
            <i className="ri-close-line text-3xl" />
          </button>
          <button
            onClick={prevImage}
            className="absolute left-6 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white transition-colors"
          >
            <i className="ri-arrow-left-line text-3xl" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-6 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white transition-colors"
          >
            <i className="ri-arrow-right-line text-3xl" />
          </button>
          <div className="max-w-5xl max-h-[85vh] px-16">
            <img
              src={galleryImages[currentImageIndex].url}
              alt={galleryImages[currentImageIndex].alt}
              className="w-full h-full object-contain"
            />
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {currentImageIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-16 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto text-center">
          <Link
            to="/"
            className="inline-block text-2xl font-extralight tracking-wide mb-4 hover:text-white/80 transition-colors"
          >
            KAZU.PHOTO
          </Link>
          <p className="text-xs text-white/60 tracking-wider mb-8">
            Photographer / Videografer
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
}